import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { AssetsGallery } from '../../AssetsGallery/AssetsGallery';
import { isTouchDevice } from '../../../utils/device';
import { useSessionInvalidation } from '../../utils/useSessionInvalidation';
import {
  enrichSalesposterHtml,
  fetchSalesposter,
} from '../../../services/sps/salesposter';
import { resolveWithTimeout } from '../../../utils/async';
import { getUserId } from '../../../services/schibstedAccount';

import {
  trackSalesposterLinkClick,
  trackSalesposterView,
} from './helpers/trackSalesposterLinkClick';

import type { Section } from '../../../services/sps/salesposter';
import type { ProviderSubscriptionMap } from '../../../services/tracking/pulseEvents';
import type { Salesposter } from '../../../services/sps/spsApi';
import type { Provider } from '@vgtv/api-client';
import type { FC } from 'react';

import * as styles from './PaywallPopup.module.scss';

type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};

const IFRAME_ID = 'salesposter_iframe';
export const DEFAULT_TIMEOUT_THRESHOLD = 3000;

interface Props {
  entityId: string;
  section: Section;
  access: ProviderSubscriptionMap[Provider.VGTV];
  onLoadFailure: (thrownValue: unknown) => void;
  isOpen: boolean;
  redirectUrl?: string;
}

export const VGPaywallPopup: FC<Props> = ({
  entityId,
  access,
  section,
  onLoadFailure,
  isOpen,
  redirectUrl,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null!);
  const [isIframeRendered, setIsIframeRendered] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [salesposter, setSalesposter] = useState<Salesposter>();

  useSessionInvalidation({ disabled: !isOpen });

  useEffect(() => {
    (async () => {
      try {
        const userId = await resolveWithTimeout({
          promise: getUserId(),
          fallback: null,
          timeout: DEFAULT_TIMEOUT_THRESHOLD,
        });

        const data = await fetchSalesposter(
          {
            userId,
            entityId,
            section,
          },
          {
            timeout: DEFAULT_TIMEOUT_THRESHOLD,
          }
        );

        setSalesposter(data);
      } catch (err) {
        onLoadFailure(err);
      }
    })();
  }, [section, entityId, onLoadFailure]);

  // proxy mousemove events within the iframe to the top document
  useEffect(() => {
    if (!isIframeLoaded || isTouchDevice || !isOpen) {
      return;
    }

    const iframe = iframeRef.current;
    const iframeRect = iframe.getBoundingClientRect();

    function onMouseMove(event: MouseEvent) {
      const proxiedEvent = new CustomEvent('mousemove', {
        bubbles: true,
        cancelable: false,
      }) as unknown as Mutable<MouseEvent>;

      proxiedEvent.clientX = event.clientX + iframeRect.left;
      proxiedEvent.clientY = event.clientY + iframeRect.top;

      // by dispatching the event on the iframe we make it bubble up to the document
      iframe.dispatchEvent(proxiedEvent);
    }

    const iframeWindow = iframe.contentWindow;

    if (iframeWindow) {
      iframeWindow.addEventListener('mousemove', onMouseMove, {
        passive: true,
      });

      return () => iframeWindow.removeEventListener('mousemove', onMouseMove);
    }
  }, [isIframeLoaded, isOpen]);

  const renderIframeContent = useCallback(async () => {
    const iframeDoc = iframeRef.current.contentDocument;

    if (iframeDoc && salesposter) {
      const { campaign: campaignId } = salesposter;

      setIsIframeRendered(true);

      iframeDoc.open();
      iframeDoc.writeln(await enrichSalesposterHtml(salesposter, redirectUrl));
      iframeDoc.close();

      iframeDoc.querySelectorAll('a').forEach((element) => {
        element.addEventListener('click', (e) => {
          trackSalesposterLinkClick(e, element, access, campaignId);
        });
      });

      trackSalesposterView(campaignId);
    }
  }, [salesposter, access, redirectUrl]);

  useEffect(() => {
    if (isOpen && !isIframeRendered) {
      renderIframeContent();
    }
  }, [isOpen, isIframeRendered, renderIframeContent]);

  const onIframeLoad = useCallback(() => {
    // the load event is also triggered when an empty iframe is first rendered,
    // so we need to check whether the content has actually been injected
    if (!isIframeLoaded) {
      setIsIframeLoaded(true);
    }
  }, [isIframeLoaded]);

  useEffect(() => {
    if (isIframeRendered) {
      setTimeout(() => setIsIframeLoaded(true), 500);
    }
  }, [isIframeRendered]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
      <iframe
        id={IFRAME_ID}
        ref={iframeRef}
        className={classNames(styles.iframe, {
          [styles.isLoaded]: isIframeLoaded,
        })}
        // the load event will trigger when all iframe resources are fully loaded
        // - this way no rendering glitches are visible to the user while styles / images are loading
        onLoad={onIframeLoad}
      />
      {isIframeRendered && (
        <AssetsGallery
          className={styles.background}
          variant={access === 'sport' ? 'sport' : 'plus'}
        />
      )}
    </>
  );
};
